import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  AppStoreBadge,
  Footer,
  Nav,
  QrProvider,
  SkipLink,
  usePageChrome,
  useReveal,
} from './typesetter/shared';
import { PAGE_PATH, PRIVACY_PATH, content } from './typesetter/content.mjs';
import {
  BarcodeVignette,
  FilesApp,
  HeroPhone,
  LockScreen,
  NotesEditor,
  NotesHeader,
  ShelfVignette,
  StatsCard,
  TextFile,
  Widgets,
  useTicker,
} from './typesetter/vignettes';

function SectionHead({ id, label, heading, body, center = false }) {
  return (
    <div className={`ts-section-head${center ? ' ts-section-head--center' : ''} ts-reveal`}>
      <span className="ts-label">{label}</span>
      <h2 id={id}>{heading}</h2>
      {body && <p className="ts-lede">{body}</p>}
    </div>
  );
}

function TimerSection({ timer }) {
  const elapsed = useTicker(24 * 60 + 17);
  return (
    <section className="ts-section" id="timer" aria-labelledby="ts-timer-heading">
      <div className="ts-container ts-feature ts-reveal">
        <div className="ts-feature-text">
          <span className="ts-label">{timer.label}</span>
          <h2 id="ts-timer-heading">{timer.heading}</h2>
          {timer.paragraphs.map((p) => (
            <p key={p} className="ts-muted">
              {p}
            </p>
          ))}
        </div>
        <div className="ts-feature-media ts-timer-media">
          <LockScreen alt={timer.lockAlt} elapsed={elapsed} />
          <NotesHeader alt={timer.notesAlt} elapsed={elapsed} />
        </div>
      </div>
    </section>
  );
}

export default function TypesetterPage() {
  usePageChrome({
    title: content.meta.title,
    description: content.meta.description,
    path: PAGE_PATH,
  });
  const rootRef = useRef(null);
  useReveal(rootRef);

  const { hero, shelves, timer, notes, stats, data, also, finalCta } = content;

  return (
    <div className="ts-page" id="ts-top" ref={rootRef}>
      <QrProvider>
        <SkipLink />
        <Nav home />

        <main id="ts-main">
          <section className="ts-hero ts-section" aria-labelledby="ts-hero-heading">
            <div className="ts-container ts-hero-grid">
              <div className="ts-hero-text">
                <h1 id="ts-hero-heading">{content.promise}</h1>
                <p className="ts-lede ts-hero-lede">{hero.supporting}</p>
                <p className="ts-muted">{hero.body}</p>
                <AppStoreBadge />
                <ul className="ts-hero-facts">
                  {hero.facts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              </div>
              <div className="ts-hero-phone">
                <HeroPhone alt={hero.imageAlt} />
              </div>
            </div>
          </section>

          <section
            className="ts-section ts-section--surface"
            id="shelves"
            aria-labelledby="ts-shelves-heading"
          >
            <div className="ts-container">
              <SectionHead
                id="ts-shelves-heading"
                label={shelves.label}
                heading={shelves.heading}
                body={shelves.body}
              />
              <div className="ts-shelves ts-reveal">
                {shelves.cards.map((card) => (
                  <article className="ts-shelf-card" key={card.name}>
                    <ShelfVignette kind={card.name} />
                    <span className="ts-label">{card.caption}</span>
                    <h3>{card.name}</h3>
                    <p className="ts-muted">{card.body}</p>
                  </article>
                ))}
              </div>
              <div className="ts-add ts-reveal">
                <div className="ts-add-text">
                  <h3>{shelves.add.heading}</h3>
                  <p className="ts-muted">{shelves.add.body}</p>
                </div>
                <BarcodeVignette alt={shelves.add.alt} />
              </div>
            </div>
          </section>

          <TimerSection timer={timer} />

          <section
            className="ts-section ts-section--surface"
            id="notes"
            aria-labelledby="ts-notes-heading"
          >
            <div className="ts-container ts-feature ts-feature--flip ts-reveal">
              <div className="ts-feature-text">
                <span className="ts-label">{notes.label}</span>
                <h2 id="ts-notes-heading">{notes.heading}</h2>
                {notes.paragraphs.map((p) => (
                  <p key={p} className="ts-muted">
                    {p}
                  </p>
                ))}
              </div>
              <div className="ts-feature-media">
                <NotesEditor alt={notes.alt} />
              </div>
            </div>
          </section>

          <section className="ts-section" id="stats" aria-labelledby="ts-stats-heading">
            <div className="ts-container">
              <div className="ts-feature ts-reveal">
                <div className="ts-feature-text">
                  <span className="ts-label">{stats.label}</span>
                  <h2 id="ts-stats-heading">{stats.heading}</h2>
                  <p className="ts-lede">{stats.body}</p>
                  <ul className="ts-points">
                    {stats.points.map((point) => (
                      <li key={point.title}>
                        <strong>{point.title}</strong>
                        <span>{point.body}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="ts-feature-media">
                  <StatsCard alt={stats.statsAlt} />
                </div>
              </div>
              <div className="ts-widget-row ts-reveal">
                <div className="ts-widget-text">
                  <h3>{stats.widget.heading}</h3>
                  <p className="ts-muted">{stats.widget.body}</p>
                </div>
                <Widgets alt={stats.widget.alt} />
              </div>
            </div>
          </section>

          <section
            className="ts-section ts-section--surface"
            id="data"
            aria-labelledby="ts-data-heading"
          >
            <div className="ts-container">
              <div className="ts-feature ts-feature--flip ts-reveal">
                <div className="ts-feature-text">
                  <span className="ts-label">{data.label}</span>
                  <h2 id="ts-data-heading">{data.heading}</h2>
                  {data.paragraphs.map((p) => (
                    <p key={p} className="ts-muted">
                      {p}
                    </p>
                  ))}
                  <Link className="ts-inline-link" to={PRIVACY_PATH}>
                    {data.link}
                  </Link>
                </div>
                <div className="ts-feature-media ts-files-media">
                  <FilesApp alt={data.filesAlt} />
                  <TextFile alt={data.fileAlt} />
                </div>
              </div>
              <ul className="ts-facts ts-reveal">
                {data.facts.map((fact) => (
                  <li key={fact.title}>
                    <strong>{fact.title}</strong>
                    <span>{fact.body}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="ts-section" id="also" aria-labelledby="ts-also-heading">
            <div className="ts-container">
              <SectionHead id="ts-also-heading" label={also.label} heading={also.heading} />
              <div className="ts-cards ts-reveal">
                {also.cards.map((card) => (
                  <article className="ts-card" key={card.label}>
                    <span className="ts-label">{card.label}</span>
                    <h3>{card.heading}</h3>
                    <p className="ts-muted">{card.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section
            className="ts-section ts-section--surface ts-cta"
            id="download"
            aria-labelledby="ts-cta-heading"
          >
            <div className="ts-container ts-narrow ts-reveal">
              <span className="ts-label">{finalCta.label}</span>
              <h2 id="ts-cta-heading">{content.promise}</h2>
              <AppStoreBadge />
              <p className="ts-muted">{finalCta.supporting}</p>
            </div>
          </section>
        </main>

        <Footer />
      </QrProvider>
    </div>
  );
}
